import { motion } from "framer-motion";
import { Quote } from "lucide-react";

/**
 * Leadership section — obsidian/copper, boardroom-grade.
 *
 * Only two figures anchor Vinci Inc.'s command: Sh. Dinesh Singh and
 * Mr. Phillip S. Their portraits deliberately use monogram medallions
 * rather than stock photography, keeping the visual register monumental,
 * neutral and consistent with the rest of the platform.
 *
 * Below the founders sits a Board of Advisors — a plain, dignified roll
 * of counsel that anchors Vinci's authority in the Indian construction
 * market and abroad. No photographs; monograms only.
 */

const LEADERS = [
  {
    initials: "DS",
    name: "Sh. Dinesh Singh",
    role: "Executive Director · Asia-Pacific",
    region: "New Delhi · Mumbai · Singapore",
    pull: "He wrote the DNA of the model.",
    body:
      "Every function of Vinci's trading ecosystem — from its smallest settlement mechanism to its largest cross-border supply programme — carries his signature. Sh. Dinesh Singh did not simply build the business; he authored its blueprint. Component by component, he engineered the logistics logic, the compliance spine and the commercial architecture that today move material across the Indian construction market and the wider Asia-Pacific corridor at a scale few can match.",
    close:
      "To those who work beside him, he is, quite simply, the man who wrote the model's DNA — a systems mind of rare precision, and the quiet force behind the enterprise's every advance.",
    tag: "Architect of the ecosystem",
  },
  {
    initials: "PS",
    name: "Mr. Phillip S.",
    role: "Global Head · Trade & Advances",
    region: "Florida, United States",
    pull: "A visionary — and mentor to many.",
    body:
      "A visionary with a truly global vantage, Mr. Phillip S. shapes the trade-and-advances doctrine that underwrites Vinci's ambition in India and beyond. Mentor to many and trusted counsel to leadership, he brings the long view — reading markets, structuring capital and steering the house with a steady, far-sighted hand.",
    close:
      "Where others see transactions, he sees trajectories; where others see risk, he sees the route through it.",
    tag: "Global vision & mentorship",
  },
];

const ADVISORS = [
  { initials: "RS", name: "Sh. Rajeev Sood", role: "Chairman Emeritus · Infrastructure Steel Alliance", region: "New Delhi" },
  { initials: "AM", name: "Justice (Retd.) A. K. Mishra", role: "Corporate Governance & Regulatory Counsel", region: "Delhi High Court" },
  { initials: "MI", name: "Dr. Meera Iyer", role: "Metallurgy & Advanced Materials", region: "IIT Kharagpur" },
  { initials: "VR", name: "Sh. Vikramaditya Rao", role: "Ex-Managing Director · National Cement Council", region: "Hyderabad" },
  { initials: "KM", name: "Mrs. Kavitha Menon", role: "Audit, Assurance & Tax Governance", region: "Bengaluru" },
  { initials: "BC", name: "Sh. Balraj Chowdhury", role: "Ex-Adviser · Ministry of Steel, Govt. of India", region: "New Delhi" },
  { initials: "HD", name: "Col. (Retd.) H. S. Deol", role: "Logistics & Cross-Border Movement", region: "Chandigarh" },
  { initials: "AK", name: "Sh. Anand Krishnamurthy", role: "Ex-CFO · Listed Cement Major", region: "Chennai" },
  { initials: "NA", name: "Dr. Nadia El-Amin", role: "International Trade Compliance", region: "Dubai" },
  { initials: "PB", name: "Sh. Prithviraj Bhosale", role: "Real-Estate & Development Finance", region: "Mumbai" },
  { initials: "PR", name: "Ms. Priya Ranganathan", role: "ESG & Sustainable Materials", region: "Pune" },
  { initials: "DS2", name: "Sh. Devinder Sehgal", role: "Ex-Chairman · Northern Steel Federation", region: "Kanpur" },
  { initials: "CW", name: "Mr. Charles Whitaker", role: "International Trade Law", region: "London" },
  { initials: "YD", name: "Sh. Yashwant Rao Deshmukh", role: "Statesman · Commerce & Industry Policy", region: "Nagpur" },
];

const Medallion = ({ initials, size = "lg", testid }) => {
  const dims = size === "xl"
    ? "w-40 h-40 md:w-56 md:h-56"
    : size === "sm"
    ? "w-14 h-14"
    : "w-24 h-24 md:w-28 md:h-28";
  const text = size === "xl"
    ? "text-6xl md:text-7xl"
    : size === "sm"
    ? "text-lg"
    : "text-3xl md:text-4xl";
  // Trim any trailing digits we added to disambiguate keys (e.g. "DS2" -> "DS").
  const glyph = String(initials || "").replace(/\d+$/, "");
  return (
    <div data-testid={testid} className={`relative ${dims} shrink-0`}>
      {/* Outer thin ring */}
      <div className="absolute inset-0 rounded-full border border-[#B87333]/50" />
      {/* Inner medallion */}
      <div
        className="absolute inset-[6%] rounded-full border border-[#B87333]/25 bg-gradient-to-br from-[#0d0d0d] to-[#050505] grid place-items-center"
        style={{ boxShadow: "inset 0 0 30px rgba(184,115,51,0.15), 0 0 42px -18px rgba(184,115,51,0.5)" }}
      >
        <span className={`font-serif ${text} text-[#B87333] tracking-tight`}>{glyph}</span>
      </div>
      {/* Corner ticks */}
      <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-px bg-[#B87333]/70" />
      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-px bg-[#B87333]/70" />
      <span className="absolute top-1/2 -translate-y-1/2 -left-1 h-4 w-px bg-[#B87333]/70" />
      <span className="absolute top-1/2 -translate-y-1/2 -right-1 h-4 w-px bg-[#B87333]/70" />
    </div>
  );
};

const LeaderCard = ({ l, i }) => (
  <motion.article
    data-testid={`leader-card-${i}`}
    initial={{ opacity: 0, y: 44 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.95, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
    className="relative overflow-hidden border border-white/10 bg-[#0a0a0a]/70 backdrop-blur-sm px-8 py-14 md:px-14 md:py-20 text-center"
  >
    {/* oversized ghost monogram */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 grid place-items-center font-serif text-[22rem] leading-none text-white/[0.025] select-none"
    >
      {String(l.initials).replace(/\d+$/, "")}
    </div>
    {/* copper hairline top + bottom */}
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#B87333]/50 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#B87333]/25 to-transparent" />

    <div className="relative z-10 flex flex-col items-center">
      <Medallion initials={l.initials} size="xl" testid={`leader-medallion-${i}`} />

      <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#B87333] mt-10">
        {l.role}
      </span>
      <h3 className="font-serif text-4xl md:text-6xl text-[#F5F5F0] mt-4 leading-[1.02] tracking-[-0.01em]">
        {l.name}
      </h3>
      <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mt-3">
        {l.region}
      </span>

      <div className="mt-10 flex items-start gap-3 max-w-2xl mx-auto text-left">
        <Quote size={22} className="text-[#B87333]/60 shrink-0 mt-1" />
        <p className="font-serif italic text-2xl md:text-3xl text-[#F5F5F0] leading-snug">
          {l.pull}
        </p>
      </div>

      <p className="font-body text-[#C9C9C2] leading-relaxed mt-8 max-w-3xl mx-auto text-left">{l.body}</p>
      <p className="font-body text-[#8A8A82] leading-relaxed mt-4 max-w-3xl mx-auto text-left">{l.close}</p>

      <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-center gap-3">
        <span className="h-px w-10 bg-[#B87333]" />
        <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#B87333]">
          {l.tag}
        </span>
        <span className="h-px w-10 bg-[#B87333]" />
      </div>
    </div>
  </motion.article>
);

const AdvisorCard = ({ a, i }) => (
  <motion.div
    data-testid={`advisor-card-${i}`}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.55, delay: (i % 3) * 0.05, ease: [0.16, 1, 0.3, 1] }}
    className="group flex items-start gap-4 border border-white/10 bg-[#0a0a0a]/40 hover:border-[#B87333]/40 transition-colors px-5 py-5"
  >
    <Medallion initials={a.initials} size="sm" testid={`advisor-medallion-${i}`} />
    <div className="min-w-0">
      <div className="font-serif text-lg md:text-xl text-[#F5F5F0] leading-tight truncate">{a.name}</div>
      <div className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#B87333] mt-1.5">{a.role}</div>
      <div className="font-body text-xs text-[#8A8A82] mt-1">{a.region}</div>
    </div>
  </motion.div>
);

export const Leadership = () => {
  return (
    <section
      id="leadership"
      data-testid="leadership-section"
      className="relative py-24 md:py-36 border-t border-white/10 overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        {/* Section header — centred, monumental */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="overline text-[#B87333]">/ Leadership</span>
          <h2 className="font-serif text-[clamp(2.25rem,5.5vw,4.75rem)] leading-[1.02] tracking-[-0.02em] mt-6">
            The two minds behind<br />the Vinci model.
          </h2>
          <p className="font-body text-[#C9C9C2] leading-relaxed text-base md:text-lg mt-8">
            Vinci Inc.'s command in the Indian construction market is anchored by two figures — one
            who authored the blueprint, and one who shapes the trade doctrine that carries it across
            borders. Everything else is the disciplined execution of their vision.
          </p>
        </motion.div>

        {/* Two centred, stacked leader cards */}
        <div className="mt-20 md:mt-24 flex flex-col gap-10 md:gap-14" data-testid="leaders-stack">
          {LEADERS.map((l, i) => (
            <LeaderCard key={l.name} l={l} i={i} />
          ))}
        </div>

        {/* Board of Advisors */}
        <div className="mt-28 md:mt-36 border-t border-white/10 pt-16 md:pt-20" data-testid="board-of-advisors">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="overline text-[#B87333]">/ Board of Advisors</span>
            <h3 className="font-serif text-[clamp(1.75rem,4vw,3rem)] leading-[1.05] tracking-[-0.02em] mt-5">
              Counsel, discipline, judgment.
            </h3>
            <p className="font-body text-[#8A8A82] leading-relaxed mt-6">
              {ADVISORS.length} senior counsel from law, policy, materials science, finance and
              logistics who anchor the Vinci house across the Indian construction market and abroad.
            </p>
          </motion.div>

          <div
            className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
            data-testid="advisors-grid"
          >
            {ADVISORS.map((a, i) => (
              <AdvisorCard key={a.name} a={a} i={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
